// src/pages/BackupRestore.js
import React, { useState, useEffect } from "react";

const TABLES = ["OTPUsers","OTPCourses","OTPProgress","OTPEmailTemplates","OTPCategories","OTPLog"];
const BLOBS  = ["otp-course-designs","otp-media"];

const TABLE_LABELS = {
  OTPUsers:          { label:"Users",           desc:"User accounts, roles, category assignments" },
  OTPCourses:        { label:"Courses",          desc:"Course metadata, availability, assignments" },
  OTPProgress:       { label:"Progress",         desc:"Trainee completion records — core compliance data" },
  OTPEmailTemplates: { label:"Email Templates",  desc:"Customised email templates" },
  OTPCategories:     { label:"Categories",       desc:"Department/category definitions" },
  OTPLog:            { label:"Audit Log",        desc:"Login and activity log" },
};
const BLOB_LABELS = {
  "otp-course-designs": { label:"Course Designs", desc:"All course content built in the designer" },
  "otp-media":          { label:"Course Media",   desc:"Images and videos embedded in courses" },
};

export default function BackupRestore() {
  const [backups,    setBackups]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [running,    setRunning]    = useState(false);
  const [runMsg,     setRunMsg]     = useState(null);

  // Restore state
  const [selFolder,  setSelFolder]  = useState("");
  const [selTables,  setSelTables]  = useState([]);
  const [selBlobs,   setSelBlobs]   = useState([]);
  const [dryRun,     setDryRun]     = useState(true);
  const [restoring,  setRestoring]  = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreError,  setRestoreError]  = useState(null);
  const [confirmOpen,   setConfirmOpen]   = useState(false);

  async function loadBackups() {
    setLoading(true); setError(null);
    try {
      const d = await fetch("/api/backup/list").then(r => r.json());
      setBackups(d.backups || []);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  useEffect(() => { loadBackups(); }, []);

  async function runManualBackup() {
    setRunning(true); setRunMsg(null);
    try {
      const d = await fetch("/api/backup/run", { method:"POST" }).then(r => r.json());
      if (d.error) throw new Error(d.error);
      setRunMsg(d.message || "Backup started.");
      await loadBackups();
      // Poll every 5s until the running backup completes
      const poll = setInterval(async () => {
        const latest = await fetch("/api/backup/list").then(r => r.json()).catch(() => null);
        if (!latest) return;
        setBackups(latest.backups || []);
        if (!(latest.backups || []).some(b => b.isRunning)) {
          clearInterval(poll);
          setRunMsg("✓ Backup complete.");
          setTimeout(() => setRunMsg(null), 5000);
        }
      }, 5000);
      setTimeout(() => clearInterval(poll), 10 * 60 * 1000); // safety stop after 10 min
    } catch (e) { setRunMsg("Failed: " + e.message); }
    finally { setRunning(false); }
  }

  function toggleTable(t) {
    setSelTables(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);
    setRestoreResult(null); setRestoreError(null);
  }
  function toggleBlob(b) {
    setSelBlobs(s => s.includes(b) ? s.filter(x => x !== b) : [...s, b]);
    setRestoreResult(null); setRestoreError(null);
  }

  const canRestore = selFolder && (selTables.length > 0 || selBlobs.length > 0);

  async function doRestore(confirmed = false) {
    if (!dryRun && !confirmed) { setConfirmOpen(true); return; }
    setConfirmOpen(false);
    setRestoring(true); setRestoreResult(null); setRestoreError(null);
    try {
      const d = await fetch("/api/backup/restore", {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ date: selFolder, tables: selTables, blobs: selBlobs, dryRun }),
      }).then(r => r.json());
      if (d.error) throw new Error(d.error);
      setRestoreResult(d);
    } catch (e) { setRestoreError(e.message); }
    finally { setRestoring(false); }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Backup &amp; Restore</div>
          <div className="page-subtitle">Nightly backups run at 03:00 UTC · 30 days retained</div>
        </div>
        <button className="btn-primary" onClick={runManualBackup} disabled={running}>
          {running ? "Running backup…" : "⬆ Run Backup Now"}
        </button>
      </div>

      {runMsg && (
        <div style={{ marginBottom:16, padding:"10px 16px", background:"#F0FDF4", border:"1px solid #86EFAC", borderRadius:8, fontSize:13, color:"#166534" }}>
          {runMsg}
        </div>
      )}

      {error && (
        <div style={{ marginBottom:16, padding:"10px 16px", background:"var(--red-light)", border:"1px solid var(--red)", borderRadius:8, fontSize:13, color:"var(--red)" }}>
          {error}
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:24, alignItems:"start" }}>

        {/* ── Available backups ── */}
        <div className="card" style={{ padding:"20px 24px" }}>
          <div style={{ fontWeight:700, fontSize:15, marginBottom:16 }}>Available Backups</div>
          {loading ? (
            <div style={{ display:"flex", justifyContent:"center", padding:24 }}><span className="spinner" style={{ width:24, height:24 }}/></div>
          ) : backups.length === 0 ? (
            <div style={{ fontSize:13, color:"var(--gray-400)", textAlign:"center", padding:"24px 0" }}>
              No backups yet — run one now or wait for tonight's scheduled backup.
            </div>
          ) : (
            <div style={{ display:"flex", flexDirection:"column", gap:6, maxHeight:520, overflowY:"auto" }}>
              {backups.map(b => {
                const isSelected = selFolder === b.folder;
                const isManual   = b.isManual;
                const timeStr    = b.timeStr
                  || (b.completedAt ? new Date(b.completedAt).toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit", second:"2-digit" }) : null);
                return (
                  <button key={b.folder} type="button"
                    onClick={() => { setSelFolder(b.folder); setRestoreResult(null); setRestoreError(null); }}
                    style={{ textAlign:"left", padding:"12px 14px", borderRadius:8, border:`2px solid ${isSelected ? "var(--gray-800)" : "var(--gray-200)"}`, background: isSelected ? "var(--gray-800)" : "#fff", cursor:"pointer", transition:"all 0.1s" }}>
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:8 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <span style={{ fontWeight:700, fontSize:14, color: isSelected ? "#fff" : "var(--gray-900)" }}>{b.date}</span>
                        <span style={{ fontSize:11, fontWeight:600, padding:"1px 7px", borderRadius:99,
                          background: isManual ? (isSelected ? "rgba(99,102,241,0.3)" : "#EEF2FF") : (isSelected ? "rgba(16,185,129,0.2)" : "#ECFDF5"),
                          color: isManual ? (isSelected ? "#C7D2FE" : "#4338CA") : (isSelected ? "#6EE7B7" : "#065F46") }}>
                          {isManual ? "Manual" : "Scheduled"}
                        </span>
                      </div>
                      {b.isRunning
                        ? <span style={{ fontSize:11, color:"#D97706", fontWeight:600 }}>⏳ Running…</span>
                        : b.tables?.errors > 0 || b.blobs?.errors > 0
                          ? <span style={{ fontSize:11, color:"#D97706", fontWeight:600 }}>⚠ Partial</span>
                          : <span style={{ fontSize:11, color: isSelected ? "#6EE7B7" : "#10B981", fontWeight:600 }}>✓ Complete</span>
                      }
                    </div>
                    <div style={{ fontSize:12, color: isSelected ? "rgba(255,255,255,0.6)" : "var(--gray-400)", marginTop:4 }}>
                      {timeStr ? `${timeStr} UTC` : ""}
                      {b.tables != null && ` · ${b.tables.ok}/${(b.tables.ok||0) + (b.tables.errors||0)} tables`}
                      {b.blobs  != null && ` · ${b.blobs.ok}/${(b.blobs.ok||0) + (b.blobs.errors||0)} blob sets`}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
          <button className="btn-ghost" style={{ fontSize:12, marginTop:12, color:"var(--gray-400)" }} onClick={loadBackups}>↺ Refresh list</button>
        </div>

        {/* ── Restore panel ── */}
        <div className="card" style={{ padding:"20px 24px" }}>
          <div style={{ fontWeight:700, fontSize:15, marginBottom:4 }}>Restore from Backup</div>
          <div style={{ fontSize:13, color:"var(--gray-500)", marginBottom:16 }}>
            {selFolder
              ? (() => {
                  const sel = backups.find(b => b.folder === selFolder);
                  const t   = sel?.timeStr ? `${sel.timeStr} UTC` : "";
                  return `${sel?.date || selFolder}${t ? ` at ${t}` : ""} — ${sel?.isManual ? "Manual" : "Scheduled"} backup`;
                })()
              : "Select a backup on the left"}
          </div>

          {/* Tables */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"var(--gray-400)", marginBottom:8 }}>Tables</div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {TABLES.map(t => (
                <label key={t} style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", padding:"10px 12px", borderRadius:8, border:`1.5px solid ${selTables.includes(t) ? "var(--gray-800)" : "var(--gray-200)"}`, background: selTables.includes(t) ? "var(--gray-50)" : "#fff" }}>
                  <input type="checkbox" checked={selTables.includes(t)} onChange={() => toggleTable(t)}
                    style={{ accentColor:"var(--gray-800)", marginTop:2, flexShrink:0, width:"auto" }}/>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>{TABLE_LABELS[t].label}</div>
                    <div style={{ fontSize:12, color:"var(--gray-400)" }}>{TABLE_LABELS[t].desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Blobs */}
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"var(--gray-400)", marginBottom:8 }}>Blob Storage</div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {BLOBS.map(b => (
                <label key={b} style={{ display:"flex", alignItems:"flex-start", gap:10, cursor:"pointer", padding:"10px 12px", borderRadius:8, border:`1.5px solid ${selBlobs.includes(b) ? "var(--gray-800)" : "var(--gray-200)"}`, background: selBlobs.includes(b) ? "var(--gray-50)" : "#fff" }}>
                  <input type="checkbox" checked={selBlobs.includes(b)} onChange={() => toggleBlob(b)}
                    style={{ accentColor:"var(--gray-800)", marginTop:2, flexShrink:0, width:"auto" }}/>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>{BLOB_LABELS[b].label}</div>
                    <div style={{ fontSize:12, color:"var(--gray-400)" }}>{BLOB_LABELS[b].desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Dry run toggle */}
          <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", marginBottom:16, padding:"10px 12px", background: dryRun ? "#EFF6FF" : "#FFF7ED", border:`1px solid ${dryRun ? "#93C5FD" : "#FED7AA"}`, borderRadius:8 }}>
            <input type="checkbox" checked={dryRun} onChange={e => { setDryRun(e.target.checked); setRestoreResult(null); }}
              style={{ accentColor:dryRun?"#3B82F6":"#EA580C", width:"auto", flexShrink:0 }}/>
            <div>
              <div style={{ fontWeight:600, fontSize:13, color: dryRun ? "#1D4ED8" : "#9A3412" }}>
                {dryRun ? "Dry run mode — no data will be changed" : "⚠ Live restore — existing data will be overwritten"}
              </div>
              <div style={{ fontSize:12, color:"var(--gray-500)", marginTop:1 }}>
                {dryRun ? "Run a dry run first to see what would be restored" : "This cannot be undone. Make sure you have the right date selected."}
              </div>
            </div>
          </label>

          {/* Action button */}
          <button className="btn-primary" style={{ width:"100%", background: dryRun ? "#3B82F6" : "var(--red)" }}
            disabled={!canRestore || restoring} onClick={() => doRestore(false)}>
            {restoring ? "Working…" : dryRun ? "Run Dry Run" : `Restore from ${selFolder}`}
          </button>

          {/* Error */}
          {restoreError && (
            <div style={{ marginTop:12, padding:"10px 14px", background:"var(--red-light)", border:"1px solid var(--red)", borderRadius:8, fontSize:13, color:"var(--red)" }}>
              {restoreError}
            </div>
          )}

          {/* Results */}
          {restoreResult && (
            <div style={{ marginTop:16 }}>
              <div style={{ fontWeight:600, fontSize:14, marginBottom:10 }}>
                {restoreResult.dryRun ? "Dry Run Results" : "✓ Restore Complete"}
              </div>
              {Object.entries(restoreResult.tables || {}).map(([name, r]) => (
                <div key={name} style={{ fontSize:13, padding:"8px 12px", borderRadius:6, marginBottom:6, background: r.status === "error" ? "var(--red-light)" : r.status === "dry_run" ? "#EFF6FF" : "#F0FDF4", border:`1px solid ${r.status === "error" ? "var(--red)" : r.status === "dry_run" ? "#93C5FD" : "#86EFAC"}` }}>
                  <span style={{ fontWeight:600 }}>{TABLE_LABELS[name]?.label || name}</span>
                  {r.status === "dry_run"  && <span style={{ color:"var(--gray-500)", marginLeft:8 }}>{r.rowsInBackup} rows would be restored</span>}
                  {r.status === "restored" && <span style={{ color:"#166534", marginLeft:8 }}>{r.inserted} rows restored · {r.deleted} old rows removed{r.errors ? ` · ${r.errors} errors` : ""}</span>}
                  {r.status === "error"    && <span style={{ color:"var(--red)", marginLeft:8 }}>{r.error}</span>}
                </div>
              ))}
              {Object.entries(restoreResult.blobs || {}).map(([name, r]) => (
                <div key={name} style={{ fontSize:13, padding:"8px 12px", borderRadius:6, marginBottom:6, background: r.status === "error" ? "var(--red-light)" : r.status === "dry_run" ? "#EFF6FF" : "#F0FDF4", border:`1px solid ${r.status === "error" ? "var(--red)" : r.status === "dry_run" ? "#93C5FD" : "#86EFAC"}` }}>
                  <span style={{ fontWeight:600 }}>{BLOB_LABELS[name]?.label || name}</span>
                  {r.status === "dry_run"  && <span style={{ color:"var(--gray-500)", marginLeft:8 }}>{r.blobsRestored} files would be restored</span>}
                  {r.status === "restored" && <span style={{ color:"#166534", marginLeft:8 }}>{r.blobsRestored} files restored{r.errors ? ` · ${r.errors} errors` : ""}</span>}
                  {r.status === "error"    && <span style={{ color:"var(--red)", marginLeft:8 }}>{r.error}</span>}
                </div>
              ))}
              {!restoreResult.dryRun && (
                <div style={{ fontSize:12, color:"var(--gray-500)", marginTop:8 }}>
                  If you restored Users or Progress, users may need to log out and back in for changes to take effect.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Live restore confirm modal */}
      {confirmOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:1000 }}>
          <div style={{ background:"#fff", borderRadius:12, padding:"28px 32px", maxWidth:440, width:"90%", boxShadow:"0 20px 60px rgba(0,0,0,0.3)" }}>
            <div style={{ fontSize:18, fontWeight:700, marginBottom:8 }}>Confirm Live Restore</div>
            <p style={{ fontSize:14, color:"var(--gray-600)", lineHeight:1.6, marginBottom:8 }}>
              You are about to restore from <strong>{selFolder}</strong>. This will <strong>permanently overwrite</strong> the following:
            </p>
            <ul style={{ fontSize:13, color:"var(--gray-700)", marginBottom:20, paddingLeft:20 }}>
              {selTables.map(t => <li key={t}>{TABLE_LABELS[t].label} table</li>)}
              {selBlobs.map(b  => <li key={b}>{BLOB_LABELS[b].label}</li>)}
            </ul>
            <p style={{ fontSize:13, color:"var(--red)", fontWeight:600, marginBottom:20 }}>
              This cannot be undone. Are you absolutely sure?
            </p>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button className="btn-secondary" onClick={() => setConfirmOpen(false)}>Cancel</button>
              <button className="btn-primary" style={{ background:"var(--red)" }} onClick={() => doRestore(true)}>
                Yes, Restore Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
